import { filterNil } from '@ngneat/elf';
import { useObservable } from '@ngneat/use-observable';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { combineLatest, from, of } from 'rxjs';
import {
  concatMap,
  last,
  map,
  shareReplay,
  takeLast,
  tap,
  toArray,
} from 'rxjs/operators';
import {
  Area,
  AreaApi,
  System,
  SystemApi,
  SystemBase,
  SystemTypeEnum,
} from '../../generated/api';
import {
  GQLListSystemsQuery,
  GQLListSystemsQueryVariables,
  GQLSystem,
  ListSystemsDocument,
  useGetSystemQuery,
  useInfiniteListSystemsQuery,
} from '../../generated/gql';
import { systemStore } from '../../state/system/systemStore';
import { siteStore } from '../../state/site/siteStore';

import { activeSite$ } from '../../state/site/siteStore.selectors';
import { activeSiteAreaSystems$ } from '../../state/site/activeSiteAreaSystems$';

import {
  defaultPagingParams,
  getNextPageParamHandler,
  optimisticUpdateInfiniteData,
  removeFalsey,
} from '../../utils';
import { AreaSystem } from '../interfaces/AreaSystem';
import { appConfiguration } from '../configuration';
import { MutationCallbackMethods } from '../settings';
import { axiosHelper } from '../../generated/axiosHelper';

interface SystemQueryVariables {
  skip: number;
  limit: number;
  companySiteId: string | null | undefined;
  companyId: string | null | undefined;
  status: string;
}

export const areasApi = new AreaApi(appConfiguration);
export const systemApi = new SystemApi(appConfiguration);
const queryKeyPrefix = 'listSystems.infinite';
const queryVariables = (
  companySiteId?: string | null,
  companyId?: string | null
): SystemQueryVariables => {
  return {
    companySiteId,
    companyId,
    status: 'active',
    ...defaultPagingParams,
  };
};
const queryKey = (companySiteId?: string | null, companyId?: string | null) => [
  queryKeyPrefix,
  queryVariables(companySiteId, companyId),
];

async function updateSystem(gqlSystem: GQLSystem) {
  if (!gqlSystem.id) return null;
  const res = await systemApi.updateSystem(gqlSystem.id, toSystem(gqlSystem));
  return res.data;
}

async function getAreas(siteId?: string | null) {
  if (!siteId) return [];
  const res = await areasApi.getAreas(0, 100, undefined, siteId, 'active');
  return res.data;
}

export function useAreas(siteId?: string | null) {
  const q = useQuery(['areas', siteId], () => getAreas(siteId), {
    onSettled(data) {
      if (data) {
        siteStore.update((s) => ({ ...s, areas: data }));
      }
    },
  });
  return q;
}

export function useGQLSystems(
  companySiteId?: string | null,
  companyId?: string | null
): GQLSystem[] {
  const queryClient = useQueryClient();
  const {
    data: systems,
    dataUpdatedAt,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteListSystemsQuery(queryVariables(companySiteId, companyId), {
    enabled: !!companySiteId || !!companyId,
    getNextPageParam: getNextPageParamHandler(
      (query) => query?.listSystems?.length
    ),
  });

  if (hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  const [_result, _setResult] = useState([] as GQLSystem[]);
  useEffect(() => {
    if (systems) {
      const _gqlSystems = of(systems)
        .pipe(
          concatMap((_systems) => from(_systems?.pages ?? [])),
          map((page) => page?.listSystems),
          filterNil(),
          concatMap((listSystems) => from(listSystems as GQLSystem[])),
          filterNil(),
          toArray(),
          map((_systems) =>
            _systems.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          ),
          last()
        )
        .subscribe({
          next: (v) => {
            systemStore.update((state) => {
              const nextSystems = v ?? state.gqlSystems ?? [];
              _setResult(nextSystems);

              return {
                ...state,
                liveKPILastUpdatedTime:
                  dataUpdatedAt ?? state?.liveKPILastUpdatedTime,
                gqlSystems: nextSystems,
              };
            });
          },
        });
    } else {
      systemStore.update((state) => ({
        ...state,
        liveKPILastUpdatedTime: dataUpdatedAt ?? state?.liveKPILastUpdatedTime,
        gqlSystems: [], //state.gqlSystems,
      }));
      _result.length !== 0 && _setResult([]);
    }
  }, [dataUpdatedAt, systems]);

  useEffect(() => {
    _result?.forEach((x) => {
      queryClient.setQueryData(['getSystem', { systemId: x.id }], x);
    });
    systemStore.update((state) => ({
      ...state,
      liveKPILastUpdatedTime: dataUpdatedAt ?? state?.liveKPILastUpdatedTime,
      gqlSystems: _result ? _result : [], //state.gqlSystems,
    }));
  }, [_result]);

  return _result;
}

export function useGQLSystem({
  systemId,
  isActive,
}: { systemId?: string | null; isActive?: true } = {}): GQLSystem | undefined {
  const { data: system, dataUpdatedAt } = useGetSystemQuery(
    {
      systemId: systemId ?? '',
    },
    { refetchInterval: 15000, enabled: !!systemId }
  );

  useEffect(() => {
    if (!!systemId && dataUpdatedAt)
      systemStore.update((state) => ({
        ...state,
        liveKPILastUpdatedTime: dataUpdatedAt,
        ...(isActive && system?.getSystem
          ? { activeSystem: system?.getSystem }
          : {}),
      }));
  }, [dataUpdatedAt, systemId]);

  return system?.getSystem ?? undefined;
}

export function useUpdateArea(
  siteId?: string,
  areaId?: string,
  callbacks?: MutationCallbackMethods
) {
  const queryClient = useQueryClient();
  return useMutation(
    async (area: Area) => {
      if (!areaId) return null;
      const response = await areasApi.updateArea(areaId, area);
      return response.data;
    },
    {
      onMutate: async (data: Area) => {
        await Promise.all([
          queryClient.cancelQueries(['systems', siteId]),
          queryClient.cancelQueries(['areas', siteId]),
        ]);

        // optimistically update areas
        const prevAreasState = queryClient.getQueryData<Area[]>([
          'areas',
          siteId,
        ]);
        const newAreasData = prevAreasState ? [...prevAreasState] : [];
        const updatedAreasIdx =
          prevAreasState?.findIndex((x) => x.id === areaId) ?? -1;
        if (updatedAreasIdx !== -1) {
          newAreasData[updatedAreasIdx] = { id: areaId, ...data };
        }
        queryClient.setQueryData(['areas', siteId], newAreasData);

        // optimistically update area systems
        const previousSystemsState = queryClient.getQueryData<AreaSystem[]>([
          'systems',
          siteId,
        ]);
        const newSystemsData = previousSystemsState
          ? [...previousSystemsState]
          : [];
        const updatedAreaIdx =
          previousSystemsState?.findIndex((x) => x.area.id === areaId) ?? -1;
        if (updatedAreaIdx !== -1) {
          newSystemsData[updatedAreaIdx] = {
            ...newSystemsData[updatedAreaIdx],
            area: { id: areaId, ...data },
          };
        }
        queryClient.setQueryData(['systems', siteId], newSystemsData);
        return { previousState: previousSystemsState };
      },
      onError: (_err, _newSettings, context) => {
        if (context?.previousState) {
          queryClient.setQueryData(['systems', siteId], context.previousState);
        }
        callbacks?.onError(_err as string);
      },
      onSuccess: () => {
        callbacks?.onSuccess();
      },
    }
  );
}

export const useUpdateSystem = (
  onSuccess: (system: System | null) => void,
  onError: (err: string) => void
) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (system: GQLSystem) => {
      const response = await updateSystem(system);
      return response;
    },
    {
      onMutate: async (system: GQLSystem) => {
        const systemData = queryClient.getQueryData<GQLSystem>([
          'getSystem',
          { systemId: system.id },
        ]);
        queryClient.setQueryData(['getSystem', { systemId: system.id }], {
          getSystem: system,
        });

        const listData = await optimisticUpdateInfiniteData<
          GQLListSystemsQuery,
          GQLSystem
        >(queryClient, queryKey(system.companySiteId), 'listSystems', system);

        return {
          systemData,
          listData,
        };
      },
      onError: (err, system, context) => {
        if (context?.listData.previousState) {
          queryClient.setQueryData(
            queryKey(system.companySiteId),
            context.listData.previousState
          );
        }
        if (context?.systemData) {
          queryClient.setQueryData(['getSystem', { systemId: system.id }], {
            getSystem: context.systemData,
          });
        }
        onError(err as string);
      },
      onSuccess: (system) => {
        onSuccess(system);
      },
    }
  );
};

export const useAreaSystems = () => {
  const [{ activeSite, activeSiteAreaSystems }] =
    useObservable(_useAreaSystems$);
  useAreas(activeSite?.id);
  useGQLSystems(activeSite?.id);
  return activeSiteAreaSystems;
};
const _useAreaSystems$ = combineLatest({
  activeSite: activeSite$,
  activeSiteAreaSystems: activeSiteAreaSystems$,
}).pipe(shareReplay(1));

function toSystem(gqlSystem: GQLSystem): SystemBase {
  return {
    companyId: gqlSystem.companyId ?? '',
    companySiteId: gqlSystem.companySiteId ?? '',
    areaId: gqlSystem.areaId ?? '',
    name: gqlSystem.name ?? '',
    serialNumber: gqlSystem.serialNumber ?? undefined,
    modelNumber: gqlSystem.modelNumber ?? undefined,
    brandId: gqlSystem.brandId ?? undefined,
    sortOrder: gqlSystem.sortOrder ?? 0,
    type: gqlSystem.type
      ? (gqlSystem.type as SystemTypeEnum)
      : SystemTypeEnum.Profi,
    deploymentDate: gqlSystem.deploymentDate ?? '',
    status: 'active',
  };
}

export async function setToDefaultSystem(siteId?: string | null) {
  const systemsQuery = axiosHelper<
    GQLListSystemsQuery,
    GQLListSystemsQueryVariables
  >(ListSystemsDocument).bind(null, {
    companySiteId: siteId,
    limit: 1,
  });
  const rawSystems = await systemsQuery();
  const gqlSystems = removeFalsey<GQLSystem>(rawSystems.listSystems);
  const activeSystem = gqlSystems.length > 0 ? gqlSystems[0] : null;
  systemStore.update((state) => ({ ...state, activeSystem: activeSystem }));
  return activeSystem;
}

interface SystemCount {
  type: string;
  count: number;
}

async function getSystemCounts(
  companyId?: string,
  siteId?: string
): Promise<SystemCount[] | null> {
  const entityId = companyId ? companyId : siteId ? siteId : undefined;
  const entityType = companyId ? 'company' : 'companySite';
  if (!entityId) return null;
  const res = await systemApi.getSystemCount(entityId, entityType);
  return [
    {
      type: 'profi',
      count: res.data.profi ?? 0,
    },
    {
      type: 'cbs',
      count: res.data.cbs ?? 0,
    },
    {
      type: 'cfs',
      count: res.data.cfs ?? 0,
    },
    {
      type: 'keraflux',
      count: res.data.keraflux ?? 0,
    },
  ];
}

export function useSystemCounts(companyId?: string, siteId?: string) {
  return useQuery(['systemCounts', companyId, siteId], () =>
    getSystemCounts(companyId, siteId)
  );
}
